import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Info } from 'lucide-react';
import { getHardwareRequest, listHardwareRequestStockOptions } from '@/api/hardwareRequests';
import { formatDate, formatDateTime, formatMoney } from '@/lib/dateFormat';
import {
  ageLabel,
  ageTone,
  checkInReading,
  deliveryReading,
  isForBranch,
  lifecycleReading,
  replacementEvidence,
  toneClass,
  warrantyReading,
} from '@/lib/hardwareRequest';
import type { HardwareRequestReplacedAsset } from '@/types/oscar';
import { AddDevices } from './AddDevices';
import { AssignedBy, AssignReplacedAsset } from './AssignReplacedAsset';
import { DecisionPanel } from './DecisionPanel';
import { FulfilmentPanel } from './FulfilmentPanel';
import { InternalNotes } from './InternalNotes';
import { PurchaseNeeded } from './PurchaseNeeded';
import { RequestedLines } from './RequestedLines';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="py-1.5">
      <dt className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">{label}</dt>
      <dd className="mt-0.5 text-sm text-darkGrey">{children}</dd>
    </div>
  );
}

/**
 * What we hold against each device - or ACCESSORY (oscar migration 124) - on the
 * request.
 *
 * ⛔ FOR THE APPROVER AND THE FULFILLER ONLY. It is deliberately NOT on the
 * request form in manage: showing a requester the shelf invites them to ask for
 * whatever happens to be in stock rather than what they need, and it is the
 * approver who has to weigh "we have one" against "we would have to buy one".
 *
 * ⚠ THREE ANSWERS THAT ARE NOT THE SAME, and were indistinguishable while this
 * read only returned pickable stock lines:
 *   • not a catalogue device  — nothing can ever be mapped to it
 *   • nothing mapped yet      — a configuration gap somebody can close
 *   • mapped, none on hand    — the only one that is actually a stock problem
 */
function Availability({
  name,
  noun,
  isInCatalogue,
  mappedLines,
  totalOnHand,
}: {
  name: string;
  /** Said in the label: an accessory that is not in the catalogue is not "a catalogue device". */
  noun: 'device' | 'accessory';
  isInCatalogue: boolean;
  mappedLines: number;
  totalOnHand: number;
}) {
  // ⚠ Reuses the page's own tone scale rather than inventing colours. "Nothing
  // mapped" is a WARNING and "not a catalogue device" is UNKNOWN: the first is
  // something somebody can close, the second is just a fact about an old
  // request naming an option that no longer exists.
  const [label, tone] =
    !isInCatalogue ? [`Not a catalogue ${noun}`, 'unknown'] as const
    : mappedLines === 0 ? ['No stock mapped', 'warn'] as const
    : totalOnHand <= 0 ? ['None on hand', 'bad'] as const
    : [`${totalOnHand} on hand`, 'ok'] as const;

  return (
    <li className="flex flex-wrap items-center gap-2 py-0.5">
      <span>{name}</span>
      <Badge tone={tone}>{label}</Badge>
      {isInCatalogue && mappedLines === 0 && (
        <span className="text-xs text-darkGrey/60">
          nothing on the Stock page says it satisfies this
        </span>
      )}
    </li>
  );
}

function Badge({ tone, children }: { tone: keyof typeof toneClass; children: React.ReactNode }) {
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${toneClass[tone]}`}>
      {children}
    </span>
  );
}

/**
 * One replaced device, with Oscar's own record of it.
 *
 * ⛔ THIS CARD IS THE POINT OF PHASE 1. Age, purchase price, warranty and the
 * last Intune check-in have all been in Oscar since the asset model was built,
 * and none of them has ever been on screen at the moment somebody decides
 * whether a replacement is warranted.
 */
function ReplacedAssetCard({ asset }: { asset: HardwareRequestReplacedAsset }) {
  const warranty = warrantyReading(asset);
  const checkIn = checkInReading(asset.IntuneLastCheckIn);
  const lifecycle = lifecycleReading(asset);

  return (
    <article className="rounded border border-border bg-white p-4 shadow-sm">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-heading text-base font-bold text-navy">
            {asset.SnapshotDisplay ?? `Asset ${asset.AssetId}`}
          </h3>
          <p className="mt-0.5 text-xs text-darkGrey/70">
            {[asset.CurrentMake, asset.CurrentModel].filter(Boolean).join(' ') || '—'}
            {asset.CurrentSerialNumber && <> · SN {asset.CurrentSerialNumber}</>}
          </p>
        </div>
        <Link to={`/assets/${asset.AssetId}`} className="text-xs font-semibold text-navy hover:underline">
          Open in Assets
        </Link>
      </header>

      {/*
        No FK ties a request to oscar.Asset (manage migration 056, deliberately),
        so an asset can be gone. Say so plainly rather than rendering a card of
        dashes that reads as a bug.
      */}
      {asset.AssetMissingFromOscar && (
        <p className="mt-3 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey/80">
          <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            Oscar no longer holds this asset. What the requester saw when they raised this is
            recorded above; nothing below could be read.
          </span>
        </p>
      )}

      {!asset.AssetMissingFromOscar && (
        <>
          {/* The replacement case, in one row. */}
          <div className="mt-3 grid gap-3 border-t border-border pt-3 sm:grid-cols-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">Age</p>
              <p className="mt-1 text-lg font-bold text-navy">{ageLabel(asset.AgeMonths)}</p>
              <p className="mt-1">
                <Badge tone={ageTone(asset.AgeMonths)}>
                  {asset.PurchaseDate ? `Bought ${formatDate(asset.PurchaseDate)}` : 'No purchase date'}
                </Badge>
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">Warranty</p>
              <p className="mt-1 text-sm text-darkGrey">
                {asset.WarrantyExpiry ? formatDate(asset.WarrantyExpiry) : 'Not recorded'}
              </p>
              <p className="mt-1"><Badge tone={warranty.tone}>{warranty.label}</Badge></p>
              {asset.DellWarrantyStatus && (
                <p className="mt-1 text-xs text-darkGrey/70">Dell: {asset.DellWarrantyStatus}</p>
              )}
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-darkGrey/60">Intune</p>
              <p className="mt-1 text-sm text-darkGrey">
                {asset.IntuneLastCheckIn ? formatDateTime(asset.IntuneLastCheckIn) : 'Never'}
              </p>
              <p className="mt-1"><Badge tone={checkIn.tone}>{checkIn.label}</Badge></p>
              {asset.IntuneCompliance && (
                <p className="mt-1 text-xs text-darkGrey/70">{asset.IntuneCompliance}</p>
              )}
            </div>
          </div>

          <dl className="mt-3 grid gap-x-6 border-t border-border pt-3 sm:grid-cols-2">
            <Field label="Purchase price">{formatMoney(asset.PurchasePrice)}</Field>
            <Field label="Current state">{asset.CurrentLifecycleState ?? '—'}</Field>
            <Field label="Asset tag">{asset.CurrentAssetTag ?? '—'}</Field>
            <Field label="Hostname">{asset.CurrentHostname ?? '—'}</Field>
            <Field label="Supplier">{asset.Supplier ?? '—'}</Field>
            <Field label="PO reference">{asset.PoReference ?? '—'}</Field>
            {/* Free text (oscar migration 067), not a person record. */}
            <Field label="Prior user">{asset.PriorUser ?? '—'}</Field>
            <Field label="OS">
              {[asset.IntuneOs, asset.IntuneOsVersion].filter(Boolean).join(' ') || '—'}
            </Field>
          </dl>
        </>
      )}

      <footer className="mt-3 border-t border-border pt-3">
        <Badge tone={lifecycle.tone}>{lifecycle.label}</Badge>
        {asset.OscarUpdateError && (
          <p className="mt-2 text-xs text-red">{asset.OscarUpdateError}</p>
        )}
        {/* Who named it - and, for an approver's pick, the way to take it back
            (oscar migration 122). */}
        <AssignedBy asset={asset} submissionId={asset.SubmissionId} />
      </footer>
    </article>
  );
}

/**
 * One hardware request, in full, with the replaced-device evidence.
 *
 * Read only — Phase 1. Nothing on this page approves, declines or allocates.
 */
export function HardwareRequestDetail() {
  const { id } = useParams<{ id: string }>();
  const submissionId = Number(id);

  const query = useQuery({
    queryKey: ['hardware-request', submissionId],
    queryFn: ({ signal }) => getHardwareRequest(submissionId, signal),
    enabled: Number.isFinite(submissionId),
  });

  // ⚠ Its own query, not folded into the request read. Stock moves on a
  // different clock from the request, and a failure here must degrade to the
  // plain device list rather than take the whole page down.
  const stock = useQuery({
    queryKey: ['hardware-request', submissionId, 'stock-options'],
    queryFn: ({ signal }) => listHardwareRequestStockOptions(submissionId, signal),
    enabled: Number.isFinite(submissionId),
  });
  const devices = stock.data?.Devices ?? [];
  const accessories = stock.data?.Accessories ?? [];

  if (query.isLoading) {
    return <p className="py-10 text-center text-sm text-darkGrey/60">Loading…</p>;
  }
  if (query.isError || !query.data) {
    return (
      <section>
        <Link to="/hardware-requests" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline">
          <ArrowLeft className="h-4 w-4" aria-hidden /> Hardware requests
        </Link>
        <p className="mt-6 text-sm text-red">Could not load this hardware request.</p>
      </section>
    );
  }

  const { Request: r, ReplacedAssets: assets } = query.data;
  const evidence = replacementEvidence(r, assets);
  const delivery = deliveryReading(r);

  return (
    <section>
      <Link to="/hardware-requests" className="inline-flex items-center gap-1 text-sm font-semibold text-navy hover:underline">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Hardware requests
      </Link>

      <header className="mb-5 mt-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold text-navy">
            {r.RequestTypeLabel} for {r.EmployeeName}
          </h1>
          <p className="mt-1 text-sm text-darkGrey/80">
            Request #{r.SubmissionId} · {r.Reason} · submitted {formatDate(r.SubmittedAt)}
          </p>
        </div>
        <Badge tone={delivery.tone}>{delivery.label}</Badge>
      </header>

      {/*
        ⚠ A null ticket id is NOT proof the request went nowhere — the SMTP2GO
        email fallback raises a Zendesk ticket from inbound mail and leaves no
        id here. Reading "no id" as "never raised" is the exact mistake made on
        17/09/2026, in the other direction.
      */}
      {r.ZendeskError && (
        <div className="mb-4 flex items-start gap-2 rounded border border-orange/30 bg-orange/5 px-3 py-2 text-sm text-darkGrey">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange" aria-hidden />
          <span>
            The Zendesk API call failed: {r.ZendeskError}
            {r.EmailSentAt && <> The email fallback did send, so a ticket may exist without an id here.</>}
          </span>
        </div>
      )}

      {/* ── The replaced-device evidence. The reason this page exists. ── */}
      {evidence.kind !== 'notARequest' && (
        <section className="mb-6">
          <h2 className="font-heading text-lg font-bold text-navy">Device being replaced</h2>

          {/*
            ⛔ A replacement with no asset is a DELIBERATE answer, not missing
            data. Since Rev 8.1 the form requires this question to be answered
            and offers "Other — it is not listed, or I would rather not say".
            Drawing it as "nothing recorded" would invite an approver to bounce
            the request back for something already asked and already answered.
          */}
          {evidence.kind === 'notListed' && (
            <p className="mt-2 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey/80">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                The requester answered <strong>&ldquo;Other — it is not listed, or I would rather
                not say&rdquo;</strong>. This is an answer, not a gap: either Oscar holds no device
                against them, or they chose not to name one. The supporting details below are where
                they were asked to describe it.
              </span>
            </p>
          )}

          {/*
            ⛔ THEIR ANSWER STAYS ON THE REQUEST (oscar migration 122). The
            paragraph above is drawn only when there are NO assets, so the moment
            an approver assigned one it would vanish and the page would read as
            though the requester had named it. They did not - and somebody
            weighing the request has to be able to tell.

            ⚠ Only when NOBODY on the requester's side named an asset. A requester
            who named one has no "Other" to report, and an approver who added a
            second does not change that. `Source` missing (an API that predates
            122) reads as the requester's, which is the safe direction.
          */}
          {evidence.kind === 'assets' && !evidence.assets.some((a) => a.Source !== 'Approver') && (
            <p className="mt-2 flex items-start gap-2 rounded border border-border bg-bg px-3 py-2 text-sm text-darkGrey/80">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              <span>
                The requester answered <strong>&ldquo;Other — it is not listed, or I would rather
                not say&rdquo;</strong>. {evidence.assets.length === 1 ? 'The device' : 'The devices'}{' '}
                below {evidence.assets.length === 1 ? 'was' : 'were'} assigned by an approver.
              </span>
            </p>
          )}

          {evidence.kind === 'assets' && (
            <div className="mt-3 grid gap-4">
              {evidence.assets.map((a) => (
                <ReplacedAssetCard key={a.AssetId} asset={a} />
              ))}
            </div>
          )}

          {/*
            ⛔ THE "REOPEN" (oscar migration 122). A requester who answered
            "Other" has told us nothing about WHICH device, but Oscar often knows
            exactly what they hold. An approver can name it here; the requester's
            answer above is left exactly as they gave it.

            ⚠ Renders nothing for anybody who cannot decide the request, for a
            new request, or for a declined one.
          */}
          <AssignReplacedAsset request={r} hasAssets={assets.length > 0} />
        </section>
      )}

      {/* ── The request itself ── */}
      <section className="rounded border border-border bg-white p-4 shadow-sm">
        <h2 className="font-heading text-lg font-bold text-navy">The request</h2>
        <dl className="mt-2 grid gap-x-6 sm:grid-cols-2">
          <Field label="Devices">
            {/* ⚠ Falls back to the comma-joined string the request view already
                carries, so a failed or pending stock read never blanks out what
                was actually asked for. */}
            {devices.length > 0
              ? (
                <ul>
                  {devices.map((d) => (
                    <Availability
                      key={d.DeviceName}
                      name={d.DeviceName}
                      noun="device"
                      isInCatalogue={d.IsInCatalogue}
                      mappedLines={d.MappedLines}
                      totalOnHand={d.TotalOnHand}
                    />
                  ))}
                </ul>
              )
              : (r.Devices ?? '—')}
          </Field>
          <Field label="Accessories">
            {/* ⛔ AN ACCESSORY CAN BE MET FROM THE SHELF TOO (oscar migration 124).
                Anthony, 15/10/2026: "worked - now do accessories stock link." It was
                plain text, with no availability and nothing to allocate.

                ⚠ THE SAME FALLBACK AS THE DEVICES, and for the same reason: an API
                that predates 124 sends no list, and one with 124 not yet applied
                sends an empty one. Both read as the comma-joined string the request
                view already carries, so a missing answer never blanks out what was
                actually asked for. */}
            {accessories.length > 0
              ? (
                <ul>
                  {accessories.map((a) => (
                    <Availability
                      key={a.AccessoryName}
                      name={a.AccessoryName}
                      noun="accessory"
                      isInCatalogue={a.IsInCatalogue}
                      mappedLines={a.MappedLines}
                      totalOnHand={a.TotalOnHand}
                    />
                  ))}
                </ul>
              )
              : (r.Accessories ?? '—')}
          </Field>
          {r.QtyOfMonitors != null && <Field label="Monitors">{r.QtyOfMonitors}</Field>}
          {r.AccessoriesCondition && <Field label="Accessory condition">{r.AccessoriesCondition}</Field>}
          {r.SimType && <Field label="SIM">{[r.SimType, r.SimSize].filter(Boolean).join(' · ')}</Field>}
          {r.FleetNumber && <Field label="Fleet number">{r.FleetNumber}</Field>}
        </dl>

        {/*
          ⛔ DEVICES AN APPROVER ADDS (oscar migration 123). A requester who forgot
          something has no way back; the approver can add it here and it is offered
          stock and named in the decision like any other device. What the requester
          ticked is left exactly as they gave it.

          ⚠ Renders nothing for anybody who cannot decide the request, or for a
          declined one.
        */}
        {/*
          ⛔ WHAT THE REQUESTER PICKED, and the approver's way to take off a pick made
          in error BEFORE the decision (28/10/2026). Withdrawn, never deleted: the
          requester's pick stays as they gave it. Renders nothing for anybody who
          cannot decide the request.
        */}
        <RequestedLines request={r} />

        <AddDevices request={r} />

        <div className="mt-3 border-t border-border pt-3">
          <Field label="Supporting details">
            <span className="whitespace-pre-wrap">{r.SupportingDetails}</span>
          </Field>
        </div>
      </section>

      {/* ── Lost or stolen ── */}
      {r.LossType && (
        <section className="mt-4 rounded border border-border bg-white p-4 shadow-sm">
          <h2 className="font-heading text-lg font-bold text-navy">Lost or stolen</h2>
          <dl className="mt-2 grid gap-x-6 sm:grid-cols-2">
            <Field label="Type">{r.LossType}</Field>
            <Field label="Last in their possession">
              {r.LastPossessedAt ? formatDateTime(r.LastPossessedAt) : '—'}
              {r.LastPossessedTimeZone && <span className="text-darkGrey/60"> ({r.LastPossessedTimeZone})</span>}
            </Field>
            <Field label="Police report">{r.PoliceReportNumber ?? '—'}</Field>
            <Field label="Lock advisory acknowledged">
              {r.LockAdvisoryAckAt ? `${formatDateTime(r.LockAdvisoryAckAt)} by ${r.LockAdvisoryAckBy ?? '—'}` : '—'}
            </Field>
            <Field label="Call diversion">{r.DiversionChoice ?? '—'}</Field>
            {/*
              ⚠ Personal data. This is on the detail only and never in the list —
              it may be a private mobile number, which was flagged at Rev 8 as
              the one field the "work contact details" argument does not cover.
            */}
            {r.DiversionNumber && <Field label="Divert to">{r.DiversionNumber}</Field>}
          </dl>
        </section>
      )}

      {/* ── Who and where ── */}
      <section className="mt-4 rounded border border-border bg-white p-4 shadow-sm">
        <h2 className="font-heading text-lg font-bold text-navy">Who and where</h2>
        <dl className="mt-2 grid gap-x-6 sm:grid-cols-2">
          <Field label={isForBranch(r.BeneficiaryKind) ? 'For branch' : 'For'}>
            {r.EmployeeName}
            {r.EmployeeEmail && <span className="text-darkGrey/60"> · {r.EmployeeEmail}</span>}
          </Field>
          <Field label="Raised by">
            {r.IsForSelf ? 'Themselves' : (r.SubmitterName ?? r.SubmitterEmail)}
          </Field>
          {/* ⛔ SUPPRESSED FOR A BRANCH, not rendered as "—". A branch has no
              manager and no job, and an em-dash in those rows reads as a gap in
              the record that somebody should go and fill — which would send an
              approver looking for a person who does not exist. */}
          {!isForBranch(r.BeneficiaryKind) && (
            <>
              <Field label="Manager at submit">{r.SupervisorName ?? r.SupervisorEmail ?? '—'}</Field>
              <Field label="Position">{r.PositionTitle ?? '—'}</Field>
            </>
          )}
          <Field label="Branch">{r.BranchName ?? '—'}</Field>
          <Field label="Cost centre">
            {[r.CostCentreCode, r.CostCentreName].filter(Boolean).join(' · ') || '—'}
          </Field>
          <Field label="Oscar person">
            {r.MatchedPersonId ? (
              <Link to={`/people/${r.MatchedPersonId}`} className="font-semibold text-navy hover:underline">
                {r.MatchedPersonName ?? `Person ${r.MatchedPersonId}`}
              </Link>
            ) : (
              /* A real answer, not an error — see migration 069. */
              <span className="text-darkGrey/60">No matching Oscar person</span>
            )}
          </Field>
          <Field label="Attachments">{r.AttachmentCount}</Field>
        </dl>
        <div className="mt-3 border-t border-border pt-3">
          <Field label="Delivery address">
            <span className="whitespace-pre-wrap">{r.DeliveryAddress}</span>
          </Field>
        </div>
      </section>

      {/* ⛔ THE DECISION SITS BELOW WHAT IT DECIDES (Anthony, 28/10/2026: "we should have
          the approve at lower parts of the screen to make sense of it"). Approving used
          to be the first thing on the page, above the request it is about; now the
          approver reads the request, the devices, who and where, and the working notes
          before being offered the button. What follows the decision stays after it:
          nothing can be allocated or bought until the request is approved. */}
      {/* The approvers' working notes. Never sent to Zendesk or shown to the requester;
          renders nothing for anybody who cannot decide the request. */}
      <InternalNotes request={r} />

      <DecisionPanel request={r} />

      {/* Phase 4. Sits AFTER the decision because it depends on it: nothing can
          be allocated until the request is approved. */}
      <FulfilmentPanel request={r} />

      {/* What an approved request needs BUYING, and whether the purchase list was
          told (28/10/2026). Renders nothing unless it is approved and something
          has no stock. */}
      <PurchaseNeeded request={r} />
    </section>
  );
}
